import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { User as FirebaseUser } from 'firebase/auth';
import ArrowLeftIcon from '../icons/ArrowLeftIcon';
import CustomerAppAuthPanel from './CustomerAppAuthPanel';
import {
  isCustomerAppAuthConfigured,
  signOutCustomerApp,
  subscribeCustomerAppAuth,
} from '../../lib/customerAppAuth';
import {
  cancelOnlineMealPlanBooking,
  cancelOnlineMealPlanSubscription,
  fetchOnlineMealPlanStatus,
  formatMealPlanCents,
  purchaseOnlineMealPlan,
  weekdayLabel,
  type OnlineMealPlanStatus,
} from '../../lib/customerAppMealPlanApi';
import { DEFAULT_MEAL_PLAN_TC_BODY } from '../../lib/mealPlanTc';
import { planFitsWeek } from '../../lib/mealPlanWeek';

function formatMenuDay(dayKey: string): string {
  const [year, month, date] = dayKey.split('-').map(Number);
  if (!year || !month || !date) return dayKey;
  return new Intl.DateTimeFormat('en-NZ', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, date, 12)));
}

interface OnlineMealPlanPanelProps {
  onBack: () => void;
  paymentReturnMessage?: string | null;
  /** Customer-facing page title from Settings. */
  pageTitle?: string;
}

const OnlineMealPlanPanel: React.FC<OnlineMealPlanPanelProps> = ({
  onBack,
  paymentReturnMessage = null,
  pageTitle,
}) => {
  const heading = (pageTitle || '').trim() || 'Student meal plans';
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [status, setStatus] = useState<OnlineMealPlanStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(paymentReturnMessage);
  const [tcAccepted, setTcAccepted] = useState(false);
  const [showTcModal, setShowTcModal] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!isCustomerAppAuthConfigured()) {
      setAuthReady(true);
      return;
    }
    return subscribeCustomerAppAuth((u) => {
      setUser(u);
      setAuthReady(true);
    });
  }, []);

  const loadStatus = useCallback(async () => {
    if (!user) {
      setStatus(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const next = await fetchOnlineMealPlanStatus();
      setStatus(next);
      setTcAccepted(next.tcVersionNeeded == null && Boolean(next.acceptedTcVersion));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load meal plans.');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const activeSub = useMemo(
    () => (status?.subscription?.status?.toLowerCase() === 'active' ? status.subscription : null),
    [status],
  );
  const matchedPlan = useMemo(
    () => (activeSub ? status?.planTypes.find((p) => p.id === activeSub.planTypeId) : null),
    [activeSub, status],
  );
  const plansForSale = useMemo(
    () => (status?.planTypes || []).filter((plan) => planFitsWeek(plan.periods, status?.nextWeek?.periods)),
    [status],
  );
  const weekPriceLabel = useMemo(() => {
    if (matchedPlan) return `${formatMealPlanCents(matchedPlan.priceCents)} for the week`;
    if (plansForSale.length === 0) return null;
    const cents = plansForSale.map((plan) => Math.max(0, Number(plan.priceCents) || 0));
    const min = Math.min(...cents);
    const max = Math.max(...cents);
    if (min === max) return `${formatMealPlanCents(min)} for the week`;
    return `From ${formatMealPlanCents(min)} for the week`;
  }, [matchedPlan, plansForSale]);
  const nextBookings = useMemo(
    () => (status?.bookings || []).filter((b) => b.weekId === status?.nextWeekId),
    [status],
  );

  const tcVersionLabel =
    status?.tcVersionNeeded || status?.planTypes[0]?.tcVersionRef || 'current';
  const tcBody = (status?.tcBody || '').trim() || DEFAULT_MEAL_PLAN_TC_BODY;

  const buy = async (planTypeId: string, tcVersionRef: string) => {
    if (status?.tcVersionNeeded && !tcAccepted) {
      setError('Please accept the meal plan terms before subscribing.');
      return;
    }
    setBusyId(planTypeId);
    setError(null);
    setInfo(null);
    try {
      const session = await purchaseOnlineMealPlan({
        planTypeId,
        acceptedTcVersion: tcVersionRef,
      });
      window.location.assign(session.checkoutUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start payment.');
      setBusyId(null);
    }
  };

  const cancelSub = async () => {
    if (!activeSub) return;
    if (
      !window.confirm(
        'Cancel your meal plan subscription?\n\n' +
          'If you cancel before Saturday cutoff, next-week meals are removed and you get a full Windcave refund for the latest payment.\n' +
          'After Saturday cutoff there is no refund; this week’s meals stay.',
      )
    ) {
      return;
    }
    setBusyId('cancel-sub');
    setError(null);
    try {
      const result = await cancelOnlineMealPlanSubscription(activeSub.id);
      const refund = result.refund;
      if (refund?.success && refund.amountCents && refund.amountCents > 0) {
        setInfo(
          `Subscription cancelled. Refund of ${formatMealPlanCents(refund.amountCents)} is on the way.`,
        );
      } else if (refund?.skippedReason === 'after_cutoff') {
        setInfo(
          'Subscription cancelled. Saturday cutoff has passed — no refund. This week’s meals stay.',
        );
      } else if (refund?.skippedReason === 'no_payment' || refund?.skippedReason === 'no_purchase') {
        setInfo(
          'Subscription cancelled. No card payment was on file to refund (e.g. staff grant).',
        );
      } else if (refund?.skippedReason === 'already_refunded') {
        setInfo('Subscription cancelled. Payment was already refunded.');
      } else {
        setInfo(
          'Subscription cancelled. Next-week meals were removed if still before cutoff.',
        );
      }
      await loadStatus();
    } catch (err) {
      try {
        await loadStatus();
      } catch {
        /* ignore */
      }
      setError(err instanceof Error ? err.message : 'Could not cancel subscription.');
    } finally {
      setBusyId(null);
    }
  };

  const cancelBooking = async (bookingId: string) => {
    setBusyId(bookingId);
    setError(null);
    try {
      await cancelOnlineMealPlanBooking(bookingId);
      setInfo('Meal cancelled for next week.');
      await loadStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not cancel meal.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl mx-auto">
        <button
          type="button"
          onClick={onBack}
          className="mb-4 flex items-center text-gray-600 hover:text-gray-800"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          Back to menu
        </button>

        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">{heading}</h1>
        <p className="text-sm text-gray-600 mb-6">
          Subscribe for next week, cancel individual meals until Saturday cutoff, or cancel your
          subscription. Same account as the Kings Park app.
        </p>

        {!isCustomerAppAuthConfigured() && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-300 text-amber-900 rounded text-sm">
            Meal plan login is not configured on this site (missing customer app Firebase env).
          </div>
        )}

        {authReady && isCustomerAppAuthConfigured() && !user && (
          <CustomerAppAuthPanel onAuthenticated={() => setError(null)} />
        )}

        {user && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-4 py-3">
            <span>
              Signed in as <span className="font-medium">{user.email}</span>
            </span>
            <button
              type="button"
              className="text-pink-600 hover:underline"
              onClick={() => void signOutCustomerApp()}
            >
              Sign out
            </button>
          </div>
        )}

        {info && (
          <div className="mb-4 p-3 bg-green-50 border border-green-300 text-green-800 rounded text-sm">
            {info}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
            {error}
          </div>
        )}

        {user && loading && (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-600">
            Loading meal plans…
          </div>
        )}

        {user && !loading && status && (
          <div className="space-y-4">
            {status.nextWeek && status.nextWeek.days.length > 0 && (
              <div className="bg-white rounded-lg shadow p-5 space-y-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="font-semibold text-gray-900">Next week’s menu</h3>
                  {weekPriceLabel && (
                    <p className="text-sm font-semibold text-pink-700">{weekPriceLabel}</p>
                  )}
                </div>
                {status.nextWeek.days.map((day) => {
                  const offered = status.nextWeek?.periods;
                  const show = (period: string) => !offered || offered.includes(period);
                  return (
                  <div key={day.dayKey} className="border-t border-gray-100 pt-3 first:border-t-0 first:pt-0">
                    <p className="font-semibold text-gray-900">
                      {formatMenuDay(day.dayKey)}
                      {day.title ? ` – ${day.title}` : ''}
                    </p>
                    {show('breakfast') && day.breakfastText ? (
                      <p className="mt-2 text-sm text-gray-700">
                        <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">Breakfast</span>
                        {day.breakfastText}
                      </p>
                    ) : null}
                    {show('lunch') && day.lunchText ? (
                      <p className="mt-2 text-sm text-gray-700">
                        <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">Lunch</span>
                        {day.lunchText}
                      </p>
                    ) : null}
                    {show('dinner') && day.dinnerText ? (
                      <p className="mt-2 text-sm text-gray-700">
                        <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">Dinner</span>
                        {day.dinnerText}
                      </p>
                    ) : null}
                  </div>
                  );
                })}
              </div>
            )}
            {activeSub && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                  Your subscription
                </p>
                <h2 className="text-xl font-bold text-gray-900">
                  {activeSub.planTypeName || matchedPlan?.name || 'Meal plan'}
                </h2>
                <p className="text-sm text-gray-700">Status: {activeSub.status}</p>
                {matchedPlan && (
                  <p className="text-sm text-gray-600">
                    {matchedPlan.daysOfWeek.map(weekdayLabel).join(', ')} ·{' '}
                    {matchedPlan.periods.join(', ')} · {formatMealPlanCents(matchedPlan.priceCents)}{' '}
                    / week
                  </p>
                )}
                {status.cutoffMs > 0 && (
                  <p className="text-xs text-gray-600">
                    Cancel next-week meals until{' '}
                    {new Date(status.cutoffMs).toLocaleString(undefined, {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </p>
                )}
                <button
                  type="button"
                  disabled={Boolean(busyId) || (Boolean(status.tcVersionNeeded) && !tcAccepted)}
                  onClick={() => {
                    const plan = matchedPlan || status.planTypes[0];
                    if (plan) void buy(plan.id, plan.tcVersionRef);
                  }}
                  className="w-full py-2.5 rounded-lg bg-pink-600 text-white font-semibold hover:bg-pink-700 disabled:opacity-60"
                >
                  {busyId && busyId !== 'cancel-sub' ? 'Opening payment…' : 'Resubscribe for next week'}
                </button>
                <button
                  type="button"
                  disabled={Boolean(busyId)}
                  onClick={() => void cancelSub()}
                  className="w-full py-2.5 rounded-lg border border-gray-300 text-gray-800 font-medium hover:bg-white disabled:opacity-60"
                >
                  {busyId === 'cancel-sub' ? 'Cancelling…' : 'Cancel subscription'}
                </button>
              </div>
            )}

            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="font-semibold text-gray-900 mb-2">Next week ({status.nextWeekId})</h3>
              {nextBookings.length === 0 ? (
                <p className="text-sm text-gray-600">No meals booked yet for next week.</p>
              ) : (
                <ul className="space-y-2">
                  {nextBookings.map((b) => (
                    <li
                      key={b.id}
                      className="flex items-center justify-between gap-3 border border-gray-100 rounded-md px-3 py-2"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {b.dayKey} · {b.period}
                        </p>
                        <p className="text-xs text-gray-500">{b.status.replace(/_/g, ' ')}</p>
                      </div>
                      {status.canCancelNextWeek && b.status === 'booked' && (
                        <button
                          type="button"
                          disabled={busyId === b.id}
                          onClick={() => void cancelBooking(b.id)}
                          className="text-sm text-pink-600 hover:underline disabled:opacity-60"
                        >
                          {busyId === b.id ? '…' : 'Cancel'}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-5 space-y-4">
              <h3 className="font-semibold text-gray-900">
                {activeSub ? 'Change plan / buy another week' : 'Subscribe for next week'}
              </h3>
              {(status.tcVersionNeeded || plansForSale.length > 0) && (
                <div className="space-y-2">
                  <label className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={tcAccepted}
                      onChange={(e) => setTcAccepted(e.target.checked)}
                    />
                    <span>
                      I accept the meal plan terms (v{tcVersionLabel}).
                    </span>
                  </label>
                  <button
                    type="button"
                    className="text-sm text-pink-600 hover:underline"
                    onClick={() => setShowTcModal(true)}
                  >
                    View terms
                  </button>
                </div>
              )}
              {plansForSale.length === 0 ? (
                <p className="text-sm text-gray-600">No plan types on sale right now.</p>
              ) : (
                plansForSale.map((plan) => (
                  <div key={plan.id} className="border border-gray-200 rounded-lg p-4 space-y-2">
                    <p className="font-semibold text-gray-900">{plan.name}</p>
                    {plan.description ? (
                      <p className="text-sm text-gray-600">{plan.description}</p>
                    ) : null}
                    <p className="text-xs text-gray-500">
                      {plan.daysOfWeek.map(weekdayLabel).join(', ')} · {plan.periods.join(', ')} ·{' '}
                      {plan.bookingMode} book
                    </p>
                    <button
                      type="button"
                      disabled={Boolean(busyId) || (Boolean(status.tcVersionNeeded) && !tcAccepted)}
                      onClick={() => void buy(plan.id, plan.tcVersionRef)}
                      className="w-full py-2 rounded-lg bg-pink-500 text-white font-semibold hover:bg-pink-600 disabled:opacity-60"
                    >
                      {busyId === plan.id
                        ? 'Opening payment…'
                        : `${activeSub ? 'Buy next week' : 'Subscribe'} · ${formatMealPlanCents(plan.priceCents)}`}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {showTcModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="meal-plan-tc-title"
          onClick={() => setShowTcModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
              <h2 id="meal-plan-tc-title" className="text-lg font-bold text-gray-900">
                Meal plan terms (v{tcVersionLabel})
              </h2>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-800 text-sm"
                onClick={() => setShowTcModal(false)}
              >
                Close
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto whitespace-pre-wrap text-sm text-gray-800 leading-relaxed">
              {tcBody}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-800 text-sm font-medium hover:bg-gray-50"
                onClick={() => setShowTcModal(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-pink-600 text-white text-sm font-semibold hover:bg-pink-700"
                onClick={() => {
                  setTcAccepted(true);
                  setShowTcModal(false);
                }}
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnlineMealPlanPanel;
